import { ArrowRight } from 'lucide-react';
import { useMemo, useState } from 'react';

import { PostFilter, type Keyword } from '../../../../../entities/post/models';
import { useHashtags } from '../../../conversation/hooks/use-hashtags';
import { useKeywords } from '../../../conversation/hooks/use-keywords';
import { showDrillDownContextMenu } from '../../../conversation/overlay/show-drill-down-context-menu';
import { useFilteredPostsNavigation } from '../../../filtered-posts/hook/use-filtered-posts-navigation';
import { useFilteredProfilesNavigation } from '../../../filtered-profiles/hook/use-filtered-profiles-navigation';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/shadcn/select';
import { WordCloud } from '@/shared/components/word-cloud/word-cloud';
import { useSelectedSavedQuery } from '@/shared/hooks/use-selected-saved-query';
import { useSharedDateRange } from '@/shared/hooks/use-shared-date-range';
import { usePublicFilter } from '../../../shared/public-filter/use-public-filter';

interface Props {
  className?: string;
  onNavigateToConversation: () => void;
}

enum TARGET_DATA {
  KEYWORDS = 'keywords',
  HASHTAGS = 'hashtags',
}

export function WordCloudSection({ className, onNavigateToConversation }: Props) {
  const [targetData, setTargetData] = useState<TARGET_DATA>(TARGET_DATA.KEYWORDS);
  const navigateToFilteredPosts = useFilteredPostsNavigation();
  const navigateToFilteredProfiles = useFilteredProfilesNavigation();
  const { query: selectedQuery } = useSelectedSavedQuery();
  const { dateRange } = useSharedDateRange();
  const { publicFilter } = usePublicFilter(selectedQuery.id);

  const keywordsQuery = useKeywords(
    {
      queryId: selectedQuery.id,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      publicFilter,
    },
    targetData === TARGET_DATA.KEYWORDS,
  );

  const hashtagsQuery = useHashtags(
    {
      queryId: selectedQuery.id,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      publicFilter,
    },
    targetData === TARGET_DATA.HASHTAGS,
  );

  const currentQuery = useMemo(() => {
    if (targetData === TARGET_DATA.KEYWORDS) return keywordsQuery;
    return hashtagsQuery;
  }, [keywordsQuery, hashtagsQuery, targetData]);

  const words = useMemo(() => {
    return currentQuery.data
      ?.map((item) => ({
        text: item.name,
        value: item.post_volume,
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 50);
  }, [currentQuery]);

  const onRowClick = async (keyword: Keyword, event: React.MouseEvent) => {
    const action = await showDrillDownContextMenu({
      position: { x: event.clientX, y: event.clientY },
    });

    const filterName =
      targetData === TARGET_DATA.KEYWORDS ? PostFilter.Keyword : PostFilter.Hashtag;

    switch (action) {
      case 'view-posts':
        await navigateToFilteredPosts({
          filter: {
            [filterName]: keyword.name,
          },
          dimension: filterName,
        });
        break;
      case 'view-profiles':
        await navigateToFilteredProfiles({
          filter: {
            [filterName]: keyword.name,
          },
          dimension: filterName,
        });
        break;
      case 'close':
        break;
    }
  };

  return (
    <div className={className}>
      <div className="rounded-lg border bg-white shadow-sm">
        <div className="flex justify-between p-6 pb-0">
          <Select value={targetData} onValueChange={(value) => setTargetData(value as TARGET_DATA)}>
            <SelectTrigger>
              <SelectValue placeholder="Select a word" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={TARGET_DATA.KEYWORDS}>Keywords</SelectItem>
              <SelectItem value={TARGET_DATA.HASHTAGS}>Hashtags</SelectItem>
            </SelectContent>
          </Select>
          <button
            className="hover:bg-muted flex size-10 items-center justify-center rounded-md"
            onClick={onNavigateToConversation}
          >
            <ArrowRight className="text-base-foreground size-4" />
          </button>
        </div>
        <div className="h-104 w-full">
          <WordCloud
            words={words ?? []}
            isLoading={currentQuery.isLoading}
            prefix={targetData === TARGET_DATA.HASHTAGS ? '#' : undefined}
            onWordClick={(word, _, event) => {
              onRowClick(
                {
                  name: word.text,
                  post_volume: Math.floor(word.value as number),
                },
                event,
              );
            }}
          />
        </div>
      </div>
    </div>
  );
}
